import { Input } from "@legal-500/legal500-design-system";
import { useFieldArray, useForm } from "react-hook-form";
import {
  BackgroundField,
  BlockItemList,
  HREF_PATTERN,
  HREF_TITLE,
  useSyncBlockContent,
} from "./blockEditorParts";
import type { ButtonsContent, ButtonVariant } from "./types";
import styles from "../CMS.module.css";

type ButtonsEditorProps = {
  value: ButtonsContent;
  onChange: (value: ButtonsContent) => void;
};

export function ButtonsEditor({ value, onChange }: ButtonsEditorProps) {
  const { control, register, watch } = useForm<ButtonsContent>({ defaultValues: value });
  const { fields, append, remove, move } = useFieldArray({ control, name: "buttons" });
  useSyncBlockContent(watch, value, onChange);

  return (
    <div className={styles.cardEditorFields}>
      <BlockItemList
        noun="Button"
        keys={fields.map(field => field.id)}
        onMove={(from, to) => move(from, to)}
        onRemove={remove}
        onAdd={() => append({ label: "", href: "", variant: "primary" })}
        renderItem={(index, key) => (
          <div className={styles.cardEditorFieldRow}>
            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor={`buttons-label-${key}`}>
                Label <span className={styles.required}>*</span>
              </label>
              <Input
                id={`buttons-label-${key}`}
                required
                pattern=".*\S.*"
                title="Enter a label"
                {...register(`buttons.${index}.label`)}
              />
            </div>
            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor={`buttons-href-${key}`}>
                Link (href) <span className={styles.required}>*</span>
              </label>
              <Input
                id={`buttons-href-${key}`}
                required
                pattern={HREF_PATTERN}
                title={HREF_TITLE}
                {...register(`buttons.${index}.href`)}
              />
            </div>
            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor={`buttons-variant-${key}`}>
                Style
              </label>
              <select
                id={`buttons-variant-${key}`}
                className={styles.selectField}
                {...register(`buttons.${index}.variant`)}
              >
                <option value={"primary" satisfies ButtonVariant}>Primary</option>
                <option value={"secondary" satisfies ButtonVariant}>Secondary</option>
              </select>
            </div>
          </div>
        )}
      />

      <BackgroundField register={register} name="background" stored={value.background} />
    </div>
  );
}
